import { Empty } from '@whistler-ui/k2/components/Empty';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { Paragraph } from '@whistler-ui/k2/components/Typography';
import { useClassHelper } from '@whistler-ui/k2/hooks';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  commonPageStylesIdentifier,
  TEST_STATUSES,
  TestTabs,
} from '../../../constants';
import {
  ExecutionTest,
  TStatuses,
  TTestTabs,
  TUseCasesApiTags,
  TUseCaseTags,
  TUseCaseType,
} from '../../../types';
import { ExecutionContent } from '../../ExecutionContent';
import TestDataPaneForm from '../../TestDataPane';
import { TestDataPaneLoading } from '../../TestDataPane/components/TestDataPaneLoading';
import { componentName } from '../constants';
// Scoped Components
import { Consoles } from './Consoles';
import { OverviewSection } from './OverviewSection';
import { TestLoader } from './TestLoader';

interface Props {
  activeContent: TTestTabs;
  execution: ExecutionTest;
  isLoading: boolean;
  status: TStatuses;
  test: any;
  type: TUseCaseType;
  tag: TUseCaseTags | TUseCasesApiTags;
}

export const TestDetailsRenderer = memo(
  ({ activeContent, execution, isLoading, status, test, type, tag }: Props) => {
    const { t } = useTranslation();
    const c = useClassHelper(componentName);
    const commonC = useClassHelper(commonPageStylesIdentifier);

    const getActiveTab = useCallback(
      (content: TTestTabs) => {
        return activeContent === content;
      },
      [activeContent],
    );

    const renderEmptyTab = useCallback(
      (title: string) => {
        return (
          <div className={commonC('__empty-state-container')}>
            <Empty.State title={title} />
          </div>
        );
      },
      [activeContent],
    );

    // Overview Tab
    if (getActiveTab(TestTabs.OVERVIEW)) {
      if (!test) {
        return renderEmptyTab(
          t('testDetails_empty_state_no_overview_data_to_show'),
        );
      }

      return (
        <OverviewSection
          test={test}
          activeContent={activeContent}
          execution={execution}
          type={type}
          isLoading={isLoading}
          status={status}
        />
      );
    }

    // Console Tab
    if (getActiveTab(TestTabs.CONSOLE)) {
      if (!test) {
        return renderEmptyTab(
          t('testDetails_empty_state_no_console_data_to_show'),
        );
      }

      if (status === TEST_STATUSES.EMPTY) {
        return (
          <Stack direction="column" gap={4}>
            <div className={c('__title')}>
              <Paragraph>
                {test.title} - {test.tag_line}
              </Paragraph>
            </div>
            <div
              className={`${c('__rows-details')} ${c(
                '__overview-content-wrapper',
              )}`}
            >
              <div className={c('__label')}>{t('testDetails_status')}</div>
              <div className={c('__label-details')}>
                <Paragraph>{t('testDetails_test_not_executed')}</Paragraph>
              </div>
            </div>
          </Stack>
        );
      }

      // UseCase Test Console
      if (Array.isArray(execution?.api_test_executions)) {
        return (
          <div className={c('__console-wrapper')}>
            {isLoading && <TestLoader status={status} />}
            <ExecutionContent data={execution} />
          </div>
        );
      }

      // Api Test Console
      if (
        Array.isArray(execution?.invocations) &&
        execution.invocations.length > 0
      ) {
        return (
          <Consoles executions={[execution]} activeContent={activeContent} />
        );
      }

      if (status === TEST_STATUSES.FAILED) {
        return renderEmptyTab(
          t('testDetails_empty_state_no_console_data_to_show'),
        );
      }

      return null;
    }

    // Test Data Form Tab
    if (getActiveTab(TestTabs.TEST_DATA)) {
      if (isLoading) {
        return <TestDataPaneLoading status={status} />;
      }

      if (!test) {
        return renderEmptyTab(
          t('testDetails_empty_state_no_test_data_to_show'),
        );
      }

      return (
        <TestDataPaneForm
          isLoading={isLoading}
          tag={tag}
          test={test}
          type={type}
        />
      );
    }

    return null;
  },
);

TestDetailsRenderer.displayName = 'TestDetailsRenderer';
